import React, { useCallback, useId } from 'react';
import { AppIcon } from '../../icons/AppIcon';
import Input from '../../ui/Input';
import type { FieldLayoutProps, FieldValueType } from './types';
import { useInlineEdit } from './useInlineEdit';

function formatFieldDisplay(value: string | number, type: FieldValueType): string {
    if (type === 'currency') {
        const num = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
        return `R$ ${num.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    }
    return String(value);
}

function valueClassName(value: string | number, type: FieldValueType): string {
    if (type === 'currency') {
        return 'deal-value text-gray-300';
    }
    return 'deal-name truncate';
}

export function FieldLayout({
    label,
    value,
    type = 'text',
    onSave,
    className = '',
}: Omit<FieldLayoutProps, 'layout'>) {
    const fieldId = useId();
    const labelId = `${fieldId}-label`;

    const toDisplayString = useCallback((v: string | number) => String(v), []);

    const parse = useCallback(
        (raw: string): string | number | null => {
            if (type === 'text') {
                const trimmed = raw.trim();
                return trimmed ? trimmed : null;
            }
            const num = parseFloat(raw);
            return Number.isNaN(num) ? null : num;
        },
        [type],
    );

    const onCommit = useCallback(
        (parsed: string | number) => {
            if (parsed !== value) onSave(parsed);
        },
        [value, onSave],
    );

    const edit = useInlineEdit({
        value,
        toDisplayString,
        parse,
        onCommit,
    });

    if (edit.isEditing) {
        return (
            <div className={className}>
                <Input
                    ref={edit.inputRef}
                    id={fieldId}
                    label={label}
                    name={label.toLowerCase().replace(/\s+/g, '-')}
                    type={type === 'text' ? 'text' : 'number'}
                    value={edit.localValue}
                    onChange={(e) => edit.setLocalValue(e.target.value)}
                    onBlur={edit.save}
                    onKeyDown={edit.handleKeyDown}
                    inputSize="sm"
                    containerClassName="gap-1"
                />
            </div>
        );
    }

    return (
        <div className={className}>
            <span id={labelId} className="mb-1 block text-body-sm text-muted">
                {label}
            </span>
            <div className="group flex items-center justify-between" aria-labelledby={labelId}>
                <div className={valueClassName(value, type)}>
                    {formatFieldDisplay(value, type)}
                </div>
                <button
                    type="button"
                    onClick={edit.startEditing}
                    className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded opacity-0 transition-all group-hover:opacity-100 hover:bg-surface-subtle sm:min-h-0 sm:min-w-0 sm:p-1"
                    aria-label={`Editar ${label}`}
                >
                    <AppIcon name="edit" className="h-4 w-4 text-muted" />
                </button>
            </div>
        </div>
    );
}
